// src/components/InteractionShareChart.tsx
import { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { TimeRange, filterByTimeRange } from "./timeRange";

interface ContentRow {
  account: string;
  date: string;
  impressionsTotal: number;
  clicksTotal: number;
  reactionsTotal: number;
  commentsTotal: number;
}

const METRICS: { key: keyof Omit<ContentRow, "account" | "date">; label: string; color: string }[] = [
  { key: "impressionsTotal", label: "Impressions", color: "#008877" },
  { key: "clicksTotal", label: "Klicks", color: "#4F7CAC" },
  { key: "reactionsTotal", label: "Reaktionen", color: "#C58B3A" },
  { key: "commentsTotal", label: "Kommentare", color: "#806A9B" },
];

interface InteractionShareChartProps {
  refreshKey: number;
  accountOptions: string[];
  timeRange: TimeRange;
}

export default function InteractionShareChart({ refreshKey, accountOptions, timeRange }: InteractionShareChartProps) {
  const [selectedAccount, setSelectedAccount] = useState<string>(accountOptions[0] ?? "");
  const [rawRows, setRawRows] = useState<ContentRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!selectedAccount) return;
    setIsLoading(true);
    setError(null);

    fetch(`/api/data/content?account=${encodeURIComponent(selectedAccount)}`)
      .then((res) => {
        if (!res.ok) throw new Error(`Fehler beim Laden (Status ${res.status})`);
        return res.json();
      })
      .then((rows: ContentRow[]) => setRawRows(rows))
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [selectedAccount, refreshKey, timeRange]);

  const handleLegendClick = (dataKey: string) => {
    setHiddenMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(dataKey)) next.delete(dataKey);
      else next.add(dataKey);
      return next;
    });
  };

  const filteredRows = filterByTimeRange(rawRows, timeRange);

  const totals: Record<string, number> = {};
  for (const metric of METRICS) {
    totals[metric.key] = filteredRows.reduce((sum, row) => sum + (row[metric.key] ?? 0), 0);
  }

  const visibleMetrics = METRICS.filter((m) => !hiddenMetrics.has(m.key));
  const pieData = visibleMetrics.map((metric) => ({
    key: metric.key,
    name: metric.label,
    value: totals[metric.key] ?? 0,
    color: metric.color,
  }));
  const totalSum = pieData.reduce((sum, entry) => sum + entry.value, 0);

  return (
    <div>
      <div className="filter-button-row">
        {accountOptions.map((account) => (
          <button
            key={account}
            className={`filter-button ${selectedAccount === account ? "selected" : ""}`}
            onClick={() => setSelectedAccount(account)}
          >
            {account}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        {isLoading && <p>Lade Daten...</p>}
        {error && <p style={{ color: "red" }}>{error}</p>}
        {!isLoading && !error && totalSum === 0 && <p>Keine Daten für diese Auswahl vorhanden.</p>}

        {!isLoading && !error && totalSum > 0 && (
          <>
            <ResponsiveContainer width="100%" height={340}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  label={({ name, value }) => `${name}: ${((value / totalSum) * 100).toFixed(1)}%`}
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.key} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => {
                    const numericValue = typeof value === "number" ? value : 0;
                    return[value]
                    //return [`${((numericValue / totalSum) * 100).toFixed(1)}%`, "Anteil"];
                  }}
                />
              </PieChart>
            </ResponsiveContainer>

            <ul style={{ listStyle: "none", display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 16, padding: 0, marginTop: 8 }}>
              {METRICS.map((metric) => {
                const isHidden = hiddenMetrics.has(metric.key);
                return (
                  <li key={metric.key} onClick={() => handleLegendClick(metric.key)} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", userSelect: "none" }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: isHidden ? "#cccccc" : metric.color, display: "inline-block" }} />
                    <span style={{ color: isHidden ? "#aaaaaa" : "#000000", fontSize: 14 }}>{metric.label}</span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}